"use strict";
/**
 * Rescale hardware helpers shared by client and server.
 *
 * `Hardware` is the Rescale contract message: total cores = slots * coresPerSlot, where
 * coresPerSlot is the node size for the core type. Historically the UI wrote the TOTAL core
 * count into coresPerSlot while still setting slots, which made Rescale multiply a second
 * time (slots 8 x coresPerSlot 768 = 6144 instead of 768). New writes must be true per-slot;
 * `normalizeHardware` rewrites legacy records on read so both shapes resolve to the same total.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.getTotalCores = exports.normalizeHardware = exports.getCoresPerSlot = exports.CORES_PER_SLOT_BY_CORE_TYPE = void 0;
/**
 * Cores per slot offered by each Rescale core type, indexed by slot count.
 * Matches Rescale's own behaviour: the value steps up per slot until the list is exhausted,
 * then stays at the last entry. Hardcoded for now; can be sourced from the Rescale API later.
 */
exports.CORES_PER_SLOT_BY_CORE_TYPE = {
    hematite: [64],
    natrolite: [96],
    raspite: [18, 36],
    'iolite-1-16core': [16]
};
/** Cores per slot Rescale allocates for `slots` slots of `coreType`, or 0 if unknown. */
const getCoresPerSlot = (slots, coreType) => {
    const steps = coreType ? exports.CORES_PER_SLOT_BY_CORE_TYPE[coreType] : undefined;
    if (!steps || slots < 1) {
        return 0;
    }
    return slots <= steps.length ? steps[slots - 1] : steps[steps.length - 1];
};
exports.getCoresPerSlot = getCoresPerSlot;
/**
 * Resolve a hardware override to true Rescale semantics (coresPerSlot is per slot).
 * A legacy record — coresPerSlot holding the already-multiplied total — is rewritten in place
 * of the per-slot value so `slots * coresPerSlot` yields the intended total either way.
 */
const normalizeHardware = (hardware) => {
    const slots = Number(hardware === null || hardware === void 0 ? void 0 : hardware.slots) || 1;
    const coresPerSlot = Number(hardware === null || hardware === void 0 ? void 0 : hardware.coresPerSlot) || 0;
    const expected = (0, exports.getCoresPerSlot)(slots, hardware === null || hardware === void 0 ? void 0 : hardware.coreType);
    const isLegacyTotal = expected > 0 && slots > 1 && coresPerSlot === slots * expected;
    return isLegacyTotal ? Object.assign(Object.assign({}, hardware), { slots, coresPerSlot: expected }) : hardware;
};
exports.normalizeHardware = normalizeHardware;
/**
 * Total cores a hardware override requests. Falls back to treating coresPerSlot as the total
 * when the core type is unrecognized, since the per-slot size can't be verified.
 */
const getTotalCores = (hardware) => {
    if (!hardware) {
        return 0;
    }
    const slots = Number(hardware.slots) || 1;
    const coresPerSlot = Number(hardware.coresPerSlot) || 0;
    if (!(0, exports.getCoresPerSlot)(slots, hardware.coreType)) {
        return coresPerSlot;
    }
    const normalized = (0, exports.normalizeHardware)(hardware);
    return (Number(normalized.slots) || 1) * (Number(normalized.coresPerSlot) || 0);
};
exports.getTotalCores = getTotalCores;
